import React, { createContext, useContext, useState, useCallback } from 'react';

type AuthTabType = 'login' | 'register' | 'updatePassword';
export type AuthTriggerContext = 'edit-product' | 'product-library' | 'default' | 'new-product' | 'manual';

interface AuthModalState {
  isOpen: boolean;
  returnPath?: string;
  initialTab: AuthTabType;
  loginOnly?: boolean;
  triggerContext?: AuthTriggerContext;
}

interface AuthContextType {
  modal: AuthModalState;
  openAuthModal: (returnPath?: string, initialTab?: AuthTabType, loginOnly?: boolean, triggerContext?: AuthTriggerContext) => void;
  closeAuthModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_MODAL_STATE: AuthModalState = {
  isOpen: false,
  initialTab: 'login',
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modal, setModal] = useState<AuthModalState>(DEFAULT_MODAL_STATE);

  const openAuthModal = useCallback((
    returnPath?: string,
    initialTab: AuthTabType = 'login',
    loginOnly?: boolean,
    triggerContext?: AuthTriggerContext
  ) => {
    // Prevent opening if modal is already open to avoid infinite loops
    setModal(prev => {
      if (prev.isOpen) {
        return prev; // Don't change state if already open
      }
      return {
        isOpen: true,
        returnPath,
        initialTab,
        loginOnly,
        triggerContext,
      };
    });
  }, []);

  const closeAuthModal = useCallback(() => {
    setModal(prev => ({ ...prev, isOpen: false, returnPath: undefined }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        modal,
        openAuthModal,
        closeAuthModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;